<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import Box from '@uiKit/elements/Box.vue'
import Skeleton from '@uiKit/elements/Skeleton.vue'
import Button from '@uiKit/elements/Button.vue'
import Message from '@uiKit/elements/Message.vue'
import Card from '@uiKit/components/Card.vue'
import EmptyState from '@uiKit/components/EmptyState.vue'
import { routes } from '@app/router/routes'
import ListingRequestCard from '../components/list/ListingRequestCard.vue'
import { useListingRequestsQuery } from '../api/useListingRequestsQuery'

// Smart container for the Sell Request ledger: owns the infinite query, maps
// pages → flat rows, and routes to detail / create. The "List New Property"
// CTA sits above the card; when there are no requests the EmptyState inside
// the card carries the only CTA, so the top one hides to avoid two primaries.
const router = useRouter()

const query = useListingRequestsQuery('')

const rows = computed(() => query.data.value?.pages.flatMap((page) => page.rows) ?? [])
const isFirstLoad = computed(() => query.isPending.value)
const isEmpty = computed(
  () => !query.isPending.value && !query.isError.value && rows.value.length === 0,
)
const errorMessage = computed(() => (query.isError.value ? 'Could not load your listings.' : ''))
const showNewListingButton = computed(() => rows.value.length > 0)

const SKELETON_ROWS = 3

function goToDetail(id: string) {
  void router.push(routes.LISTING_DETAIL.replace(':id', id))
}

function goToNew() {
  void router.push(routes.LISTING_NEW)
}

function loadMore() {
  if (query.hasNextPage.value && !query.isFetchingNextPage.value) void query.fetchNextPage()
}
</script>

<template>
  <Box direction="col" gap="4">
    <Box
      v-if="showNewListingButton"
      flex
      align="center"
      justify="end"
      gap="3"
      class="listings__head"
    >
      <Button label="Sell Request" color="primary" icon="plus" @click="goToNew" />
    </Box>

    <Card variant="outlined" padding="6" class="listings">
      <Message v-if="errorMessage" color="danger" variant="outlined" class="listings__message">
        {{ errorMessage }}
      </Message>

      <Box v-if="isFirstLoad" direction="col" gap="3" class="listings__body">
        <Skeleton v-for="index in SKELETON_ROWS" :key="index" height="104px" />
      </Box>

      <EmptyState
        v-else-if="isEmpty"
        icon="home"
        title="No sell requests yet"
        description="Start by listing a property — track its review, agreement, and status here."
        primary-action-label="List New Property"
        primary-action-icon="plus"
        @primary-action="goToNew"
      />

      <Box v-else direction="col" gap="3" class="listings__body">
        <ListingRequestCard
          v-for="request in rows"
          :key="request.id"
          :request="request"
          @view="goToDetail"
        />

        <Box v-if="query.hasNextPage.value" flex justify="center" class="listings__more">
          <Button
            label="Load more"
            variant="outline"
            :loading="query.isFetchingNextPage.value"
            @click="loadMore"
          />
        </Box>
      </Box>
    </Card>
  </Box>
</template>

<style scoped>
.listings__head {
  flex-wrap: wrap;
}

.listings__message {
  margin-bottom: 0.75rem;
}

.listings__more {
  padding-top: 0.5rem;
}
</style>
